import { type ReactNode, useMemo } from 'react';
import { QueryEditorContextProvider, type QueryEditorState } from './query-editor-context';

export interface QueryEditorRootProps {
  value: string;
  onChange: (value: string) => void;
  onRun?: () => void;
  /**
   * Controlled editor mode (e.g. code vs builder). Required for `QueryEditor.ModeContent`. Required together with
   * `onModeChange` for `QueryEditor.ModeToggle`.
   */
  mode?: string;
  onModeChange?: (mode: string) => void;
  children: ReactNode;
}

/**
 * Context provider for a single query editor. Passes controlled props directly
 * to descendants via React Context — no intermediate store or sync layer.
 *
 * All QueryEditor.* sub-components must be descendants of a Root.
 */
export function QueryEditorRoot({ value, onChange, onRun, mode, onModeChange, children }: QueryEditorRootProps) {
  const ctx = useMemo<QueryEditorState>(
    () => ({ value, onChange, onRun, mode, onModeChange }),
    [value, onChange, onRun, mode, onModeChange]
  );

  return <QueryEditorContextProvider value={ctx}>{children}</QueryEditorContextProvider>;
}
